import CheckIcon from '@mui/icons-material/Check'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import { Alert, Box, Button, IconButton, Stack, Tooltip, Typography } from '@mui/material'
import { useState } from 'react'

interface Props {
  password: string
  /** What the reader should do with it, e.g. "Send this to riku." */
  instruction: string
}

/**
 * Shows a generated password once, with a copy button.
 *
 * There is no way back to it: the server stores only a hash, so if this is
 * dismissed without copying, the only remedy is another reset. The warning
 * says so, because a dialog that looks like a receipt gets closed like one.
 */
export default function OneTimePassword({ password, instruction }: Props) {
  const [copied, setCopied] = useState(false)

  async function copy() {
    try {
      await navigator.clipboard.writeText(password)
      setCopied(true)
    } catch {
      // Clipboard access can be refused (insecure context, or the user said no).
      // The password is on screen and selectable, so this is not worth an error.
      setCopied(false)
    }
  }

  return (
    <Stack spacing={2}>
      <Alert severity="warning">
        This password is shown once and cannot be recovered. {instruction}
      </Alert>

      <Stack direction="row" spacing={1} alignItems="center">
        <Box
          component="code"
          sx={{
            flexGrow: 1,
            px: 2,
            py: 1.5,
            borderRadius: 1,
            bgcolor: 'action.hover',
            fontFamily: 'monospace',
            fontSize: '1.1rem',
            letterSpacing: 1,
            userSelect: 'all',
            wordBreak: 'break-all',
          }}
        >
          {password}
        </Box>
        <Tooltip title={copied ? 'Copied' : 'Copy to clipboard'}>
          <IconButton onClick={() => void copy()} color={copied ? 'success' : 'default'}>
            {copied ? <CheckIcon /> : <ContentCopyIcon />}
          </IconButton>
        </Tooltip>
      </Stack>

      <Typography variant="caption" color="text.secondary">
        They will be asked to choose their own password the first time they sign in.
      </Typography>
    </Stack>
  )
}

export { OneTimePassword }
export type { Props as OneTimePasswordProps }

/** Exported for the dialogs that wrap this, so the copy button can be primary. */
export function CopyButton({ password }: { password: string }) {
  const [copied, setCopied] = useState(false)
  return (
    <Button
      startIcon={copied ? <CheckIcon /> : <ContentCopyIcon />}
      onClick={() => {
        void navigator.clipboard.writeText(password).then(
          () => setCopied(true),
          () => setCopied(false),
        )
      }}
    >
      {copied ? 'Copied' : 'Copy password'}
    </Button>
  )
}
